import { useEffect, useCallback } from 'react'

const SEO = ({
  title,
  description,
  keywords = '',
  image = '',
  imageAlt = '',
  url = '',
  type = 'website',
  schemaType = 'WebPage',
  schemaData = {},
  breadcrumbs = [],
  author = 'Your Claim Matters',
  publishedTime = '',
  modifiedTime = '',
  article = false,
  faq = false,
  faqs = [],
}) => {
  // Effects do not run during SSR. Capture the matched page's SEO props so the
  // build-time prerenderer can put them in <head> before shipping the HTML.
  if (typeof document === 'undefined') {
    globalThis.__YCM_SSR_SEO__ = {
      title,
      description,
      keywords,
      image,
      imageAlt,
      url,
      type,
      schemaType,
      schemaData,
      breadcrumbs,
      author,
      publishedTime,
      modifiedTime,
      article,
      faq,
      faqs: faqs.map(item => ({
        question: item.question,
        answerText: item.answerText || '',
      })),
    }
  }

  // Generate structured data based on page type
  const generateStructuredData = useCallback(() => {
    const baseUrl = 'https://yourclaimmatters.co.uk'
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

    // Add FAQ structured data if this is an FAQ page
    if (faq && faqs.length > 0) {
      const faqStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        name: title,
        description: description,
        url: fullUrl,
        mainEntity: faqs.map(faqItem => ({
          '@type': 'Question',
          name: faqItem.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faqItem.answer,
          },
        })),
      }

      // Add breadcrumb if provided
      if (breadcrumbs.length > 0) {
        faqStructuredData.breadcrumb = {
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.url.startsWith('http')
              ? crumb.url
              : `${baseUrl}${crumb.url}`,
          })),
        }
      }

      return [faqStructuredData]
    }

    let structuredData = {
      '@context': 'https://schema.org',
      '@type': schemaType,
      name: title,
      description: description,
      url: fullUrl,
      ...schemaData,
    }

    // Add breadcrumb structured data if breadcrumbs are provided
    if (breadcrumbs.length > 0) {
      structuredData.breadcrumb = {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url.startsWith('http')
            ? crumb.url
            : `${baseUrl}${crumb.url}`,
        })),
      }
    }

    // Add Article structured data for blog posts
    if (article) {
      structuredData = {
        ...structuredData,
        '@type': 'Article',
        headline: title,
        author: {
          '@type': 'Organization',
          name: author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Your Claim Matters',
          url: baseUrl,
        },
      }

      if (publishedTime) {
        structuredData.datePublished = publishedTime
      }
      if (modifiedTime) {
        structuredData.dateModified = modifiedTime
      }
      if (image) {
        structuredData.image = image.startsWith('http')
          ? image
          : `${baseUrl}${image}`
      }
    }

    return [structuredData]
  }, [
    title,
    description,
    url,
    schemaType,
    schemaData,
    breadcrumbs,
    faq,
    faqs,
    article,
    author,
    publishedTime,
    modifiedTime,
    image,
  ])

  useEffect(() => {
    // Update document title
    document.title = title

    // Ensure html lang attribute is set
    const htmlElement = document.documentElement
    if (!htmlElement.hasAttribute('lang')) {
      htmlElement.setAttribute('lang', 'en')
    }

    // Update or create meta tags
    const updateMetaTag = (name, content, property = false) => {
      if (!content) return

      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`
      let meta = document.querySelector(selector)
      if (!meta) {
        meta = document.createElement('meta')
        if (property) {
          meta.setAttribute('property', name)
        } else {
          meta.setAttribute('name', name)
        }
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    // Basic meta tags
    updateMetaTag('description', description)
    if (keywords) updateMetaTag('keywords', keywords)
    updateMetaTag('robots', 'index, follow')
    updateMetaTag('author', author)
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0')

    // Open Graph tags
    updateMetaTag('og:title', title, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:type', type, true)
    if (url)
      updateMetaTag(
        'og:url',
        url.startsWith('http') ? url : `https://yourclaimmatters.co.uk${url}`,
        true
      )
    updateMetaTag('og:site_name', 'Your Claim Matters', true)

    if (image) {
      const fullImageUrl = image.startsWith('http')
        ? image
        : `https://yourclaimmatters.co.uk${image}`
      updateMetaTag('og:image', fullImageUrl, true)
      updateMetaTag('twitter:image', fullImageUrl)
    }
    if (imageAlt) {
      updateMetaTag('og:image:alt', imageAlt, true)
      updateMetaTag('twitter:image:alt', imageAlt)
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', title)
    updateMetaTag('twitter:description', description)

    // Article specific meta tags
    if (article) {
      if (publishedTime)
        updateMetaTag('article:published_time', publishedTime, true)
      if (modifiedTime)
        updateMetaTag('article:modified_time', modifiedTime, true)
      updateMetaTag('article:author', author, true)
    }

    // Update canonical link
    if (url) {
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute(
        'href',
        url.startsWith('http') ? url : `https://yourclaimmatters.co.uk${url}`
      )
    }

    // Add structured data
    const structuredDataArray = generateStructuredData()

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll(
      'script[data-seo-structured-data]'
    )
    existingScripts.forEach(script => script.remove())

    // Add new structured data scripts
    structuredDataArray.forEach((data, index) => {
      const script = document.createElement('script')
      script.setAttribute('type', 'application/ld+json')
      script.setAttribute('data-seo-structured-data', `true-${index}`)
      script.textContent = JSON.stringify(data)
      document.head.appendChild(script)
    })

    // Cleanup function
    return () => {
      // Remove structured data scripts when component unmounts
      const scripts = document.querySelectorAll(
        'script[data-seo-structured-data]'
      )
      scripts.forEach(script => script.remove())
    }
  }, [
    title,
    description,
    keywords,
    image,
    imageAlt,
    url,
    type,
    schemaType,
    schemaData,
    breadcrumbs,
    author,
    publishedTime,
    modifiedTime,
    article,
    faq,
    faqs,
    generateStructuredData,
  ])

  return null // This component doesn't render anything visible
}

export default SEO
